// Geolocation source used to enrich board connection events.
// It is best effort: it never throws and never blocks the caller, it resolves to
// an item with an `error` field when the position is unavailable.

export const GEO_SOURCE = 'geo';

// Chrome counts the time the permission prompt is on screen against this
// timeout, so it has to be long enough for the user to answer it.
const BROWSER_TIMEOUT = 20000;
// The device does not move between one board plug and the next, so the position
// is cached to avoid resolving it again on every event.
const CACHE_TTL = 10 * 60 * 1000;
// A failed attempt is only cached briefly: the next event retries it instead of
// repeating the same error for the rest of the session.
const ERROR_CACHE_TTL = 30 * 1000;

const cache = { value: null, timestamp: 0, promise: null };

const now = () => new Date().getTime();

const isFresh = () => {
  if (cache.value === null) return false;
  const ttl = cache.value.error ? ERROR_CACHE_TTL : CACHE_TTL;
  return (now() - cache.timestamp) < ttl;
};

const geoError = (error) => ({
  source: GEO_SOURCE,
  latitude: null,
  longitude: null,
  error,
});

// Browser geolocation (gps / wifi). Skipped when the user already denied it so
// plugging a board never hangs waiting for a prompt that will not be answered.
const readBrowserGeolocation = () => new Promise((resolve) => {
  if (!navigator.geolocation) {
    resolve(geoError('unsupported'));
    return;
  }

  const request = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        source: GEO_SOURCE,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy || null,
      }),
      (error) => resolve(geoError(error?.message || 'unavailable')),
      { timeout: BROWSER_TIMEOUT, maximumAge: CACHE_TTL },
    );
  };

  if (navigator.permissions?.query) {
    navigator.permissions.query({ name: 'geolocation' }).then((status) => {
      if (status.state === 'denied') {
        resolve(geoError('denied'));
      } else {
        request();
      }
    }).catch(() => request());
  } else {
    request();
  }
});

// Resolves the cached position, the in flight request, or a new one.
export const getBrowserGeolocation = () => {
  if (isFresh()) return Promise.resolve(cache.value);
  if (cache.promise) return cache.promise;
  cache.promise = readBrowserGeolocation().then((value) => {
    cache.value = value;
    cache.timestamp = now();
    cache.promise = null;
    return value;
  });
  return cache.promise;
};

// Available sources as separated items, each one tagged with where it comes from.
export const getGeolocationSources = () => getBrowserGeolocation().then((geo) => [geo]);
